import { appConfig } from 'config/environment/appConfig'

import { HttpService } from 'services/HttpService'
import { getQueryUrl } from 'services/utils/getQueryUrl'
import convertKeysToCamelCase from 'utils/helpers/convertKeysToCamelCase'
import { ResponseListData } from 'shared/types/types'

import {
  AddEmployeeRequest,
  EmployeeDTO,
  UpdateUserPvzRolesRequest,
  UpdateUserPvzRolesRequestDTO,
  UserELockRoleDTO,
  UserELockRoleRequest,
  UserELockRolesDTO,
  UserPvzRolesDTO,
} from './types/typesDTO'
import { ELOCK_PERMISSIONS } from './data'

const apiService = new HttpService(appConfig.API_URL)
const discoveryService = new HttpService(appConfig.DISCOVERY_URL)

export const employeeApi = {
  getPvzEmployees: async (shard: number): Promise<ResponseListData<EmployeeDTO[]>> => {
    const queryUrl = getQueryUrl({
      show_deleted: true,
    })

    const result = await apiService.get<ResponseListData<EmployeeDTO[]>>(
      `s${shard}/api/v2/pickpoint/user/list?${queryUrl}`,
    )

    return convertKeysToCamelCase(result)
  },

  addEmployee: async (data: AddEmployeeRequest): Promise<{ token: string }> => {
    return await discoveryService.post('/api/v3/pickpoint/user/add/verify', {
      body: {
        first_name: data.firstName,
        last_name: data.lastName,
        permission: data.permission,
        phone: data.phone,
        pickpoint_id: data.pickPointId,
        country: data.country,
        snils: data.snils,
      },
    })
  },

  verifyEmployee: async (data: AddEmployeeRequest): Promise<number> => {
    const res = await discoveryService.post<{ user_id: number }>('/api/v3/pickpoint/user/add', {
      body: {
        first_name: data.firstName,
        last_name: data.lastName,
        permission: data.permission,
        phone: data.phone,
        pickpoint_id: data.pickPointId,
        country: data.country,
        snils: data.snils,
        token: data.token,
        code: data.code,
      },
    })

    return res?.user_id
  },

  getUserPvzRoles: async (employeeId: number): Promise<UserPvzRolesDTO[]> => {
    const result = await discoveryService.post<ResponseListData<UserPvzRolesDTO>>(
      '/api/v2/pickpoint/pickpoint-and-user/list',
      {
        body: {
          employee_id: employeeId,
        },
      },
    )

    return convertKeysToCamelCase(result?.data)
  },

  updateUserPvzRoles: async (data: UpdateUserPvzRolesRequest[]): Promise<UpdateUserPvzRolesRequestDTO[]> => {
    const result = await discoveryService.post<ResponseListData<UpdateUserPvzRolesRequestDTO>>(
      '/api/v2/pickpoint/role/update',
      {
        body: {
          data,
        },
      },
    )

    return convertKeysToCamelCase(result?.data)
  },

  getUserELockRoles: async (userId: number): Promise<UserELockRoleDTO[]> => {
    const result = await discoveryService.get<UserELockRolesDTO>(
      `/api/v2/pickpoint/user/elock-permission/list?user_id=${userId}`,
    )

    return convertKeysToCamelCase(result?.roles)
  },

  getCurrentUserELockRole: async (): Promise<ELOCK_PERMISSIONS> => {
    const result = await discoveryService.get<{ elock_permission: ELOCK_PERMISSIONS }>(
      '/api/v2/pickpoint/user/elock-permission/get',
    )

    return result.elock_permission
  },

  updateUserELockRoles: async (data: UserELockRoleRequest[]): Promise<{ success: boolean }> => {
    return await discoveryService.post('/api/v2/pickpoint/user/elock-permission/update', {
      body: {
        roles: data,
      },
    })
  },

  changeUserSnils: async (snils: number, userId: number): Promise<{ success: boolean }> => {
    return await discoveryService.post('/api/v2/pickpoint/user/snils/update', {
      body: {
        snils,
        wb_user_id: userId,
      },
    })
  },

  deleteUser: async (userId: number): Promise<ResponseListData<boolean>> => {
    return await discoveryService.post(`/api/v2/pickpoint/user/${userId}/delete`, {
      body: {
        user_id: userId,
      },
    })
  },

  recoverUser: async (userId: number): Promise<ResponseListData<boolean>> => {
    return await discoveryService.post('/api/v2/pickpoint/user/recover', {
      body: {
        user_id: userId,
      },
    })
  },
}
